import { useSEO } from "@/hooks/useSEO";
import { SeoPageLayout } from "@/components/seo/SeoPageLayout";
import { SeoSection, SeoSubSection, SeoList } from "@/components/seo/SeoContent";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";

const SITE = "https://regard-de-nature.fr";

const NaturopatheMontesquieu = () => {
  useSEO({
    title: "Naturopathe à Montesquieu-Lauragais (31450) – Laura Elie",
    description:
      "Naturopathe à Montesquieu-Lauragais : cabinet au calme, en pleine campagne du Lauragais. Stress, fatigue, sommeil, digestion. Consultations sur rendez-vous avec Laura Elie.",
    canonical: `${SITE}/naturopathe-montesquieu-lauragais`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "Regard de Nature – Laura Elie, Naturopathe (Montesquieu-Lauragais)",
      image: `${SITE}/regard-de-nature-logo.png`,
      "@id": `${SITE}/naturopathe-montesquieu-lauragais`,
      url: `${SITE}/naturopathe-montesquieu-lauragais`,
      telephone: "+33637031866",
      address: {
        "@type": "PostalAddress",
        streetAddress: "31 rue de l'ancien petit fossé",
        addressLocality: "Montesquieu-Lauragais",
        postalCode: "31450",
        addressCountry: "FR",
      },
      areaServed: [
        "Montesquieu-Lauragais",
        "Villefranche-de-Lauragais",
        "Nailloux",
        "Caraman",
        "Auterive",
        "Baziège",
        "Auriac-sur-Vendinelle",
        "Lauragais",
        "Sud toulousain",
      ],
      priceRange: "€€",
    },
  });

  return (
    <SeoPageLayout
      h1="Naturopathe à Montesquieu-Lauragais – Un cabinet au calme, en pleine campagne"
      intro="Vous habitez Montesquieu-Lauragais, Nailloux, Villefranche, Auterive ou un village du Sud toulousain ? Je vous reçois dans un cabinet confidentiel, au cœur du Lauragais, pour prendre le temps de comprendre ce qui pèse et remettre votre vitalité en mouvement."
    >
      <SeoSection title="Un cabinet de naturopathie au cœur du Lauragais">
        <p>
          Je reçois souvent des personnes qui arrivent un peu essoufflées : trop de choses à porter, un sommeil
          devenu fragile, un ventre qui ne suit plus, une énergie en baisse depuis des mois. Si vous vous
          reconnaissez, sachez qu'il existe des leviers concrets pour avancer — sans tout révolutionner.
        </p>
        <p>
          Je suis Laura Elie, naturopathe et iridologue. Mon cabinet principal se trouve à{" "}
          <strong>Montesquieu-Lauragais</strong>, dans la Haute-Garonne (31450), à mi-chemin entre Toulouse et
          Castelnaudary. Ici, l'ambiance est différente de celle d'un centre-ville : campagne ouverte, lumière
          douce, bruits apaisés. Un cadre qui invite à <strong>ralentir</strong> dès l'arrivée — et c'est souvent
          là que l'écoute de soi commence.
        </p>
        <p>
          Pour rappel, la naturopathie n'est pas une pratique médicale et ne remplace jamais le suivi de votre
          médecin. C'est un accompagnement de mieux-être, qui s'appuie sur l'alimentation, l'hygiène de vie, la
          gestion du stress et les outils naturels (plantes, hydrolats, élixirs floraux) pour soutenir votre
          vitalité.
        </p>
      </SeoSection>

      <SeoSection title="Ce que vous pouvez retrouver, séance après séance">
        <p>
          L'objectif n'est pas une transformation spectaculaire, mais un retour progressif à un équilibre tenable.
          Au fil de l'accompagnement, les personnes que je reçois constatent souvent :
        </p>
        <SeoList
          items={[
            "Une énergie plus stable, sans les coups de pompe brutaux de l'après-midi.",
            "Des nuits plus continues, avec un endormissement plus simple.",
            "Un confort digestif retrouvé après les repas.",
            "Un mental plus posé, capable de relâcher en fin de journée.",
            "Une vraie reprise de main sur son hygiène de vie, sans rigidité.",
          ]}
        />
      </SeoSection>

      <SeoSection title="Pour quelles problématiques consulter ?">
        <p>
          Au cabinet de Montesquieu-Lauragais, les motifs de consultation reflètent souvent un mode de vie où le
          corps et le mental n'ont plus le temps de souffler. Voici les principaux axes sur lesquels je travaille :
        </p>

        <SeoSubSection title="Stress qui s'installe et difficile à relâcher">
          <p>
            Quand la tension devient un état de fond — épaules nouées, mâchoire serrée, mental qui tourne en
            boucle — le système nerveux a besoin d'être soutenu. Nous explorons des outils concrets : respiration,
            ancrage corporel, plantes adaptogènes, élixirs floraux, et surtout des micro-changements dans la
            journée pour redonner de la place à la décompression.
          </p>
          <p>
            👉{" "}
            <Link to="/naturopathe-stress" className="text-primary underline hover:text-secondary">
              Voir l'accompagnement du stress
            </Link>
          </p>
        </SeoSubSection>

        <SeoSubSection title="Fatigue qui ne passe pas malgré le repos">
          <p>
            Une fatigue persistante n'a presque jamais une seule cause. Nous regardons ensemble la qualité de votre
            sommeil, votre alimentation, votre hydratation, votre charge émotionnelle, votre activité physique. Le
            but : identifier les deux ou trois leviers qui, activés ensemble, peuvent vraiment relancer votre
            énergie.
          </p>
          <p>
            👉{" "}
            <Link to="/naturopathe-fatigue" className="text-primary underline hover:text-secondary">
              Découvrir l'accompagnement de la fatigue
            </Link>
          </p>
        </SeoSubSection>

        <SeoSubSection title="Inconfort digestif au quotidien">
          <p>
            Ventre gonflé en fin de journée, transit irrégulier, sensation de digérer lentement, intolérances qui
            s'invitent… La digestion est l'un des piliers de la naturopathie. Nous travaillons l'équilibre du
            microbiote, le rythme des repas, la qualité de la mastication et le soutien doux du foie quand c'est
            pertinent.
          </p>
          <p>
            👉{" "}
            <Link to="/naturopathe-digestion" className="text-primary underline hover:text-secondary">
              Voir l'accompagnement digestion
            </Link>
          </p>
        </SeoSubSection>

        <SeoSubSection title="Sommeil perturbé et nuits hachées">
          <p>
            Endormissement qui s'éternise, réveils en pleine nuit, sensation de ne jamais dormir « assez »… Le
            sommeil se construit dès le matin, pas seulement le soir. Nous reposons les bases d'une hygiène de
            sommeil réaliste, accompagnée si besoin de plantes calmantes, de magnésium ou d'élixirs floraux ciblés.
          </p>
          <p>
            👉{" "}
            <Link to="/naturopathe-sommeil" className="text-primary underline hover:text-secondary">
              Explorer l'accompagnement du sommeil
            </Link>
          </p>
        </SeoSubSection>
      </SeoSection>

      <SeoSection title="Et si l'une de ces situations était la vôtre ?">
        <SeoList
          items={[
            "Vous traînez une fatigue persistante depuis des mois et le week-end ne suffit plus à la dissiper.",
            "Vous avez l'impression de vivre en mode « tendu », sans savoir comment sortir de cet état.",
            "Vos digestions deviennent un sujet : vous craignez certains repas, vous évitez certains aliments.",
            "Vos nuits sont morcelées, vous vous réveillez plusieurs fois et vous démarrez la journée déjà fatigué·e.",
            "Vous traversez une période charnière (changement de vie, périnatalité, ménopause) et cherchez un appui doux.",
            "Vous voulez simplement reprendre la main sur votre hygiène de vie, sans tomber dans des injonctions rigides.",
          ]}
        />
        <p>
          Si vous vous reconnaissez dans une ou plusieurs de ces situations, une consultation peut vous aider à
          poser un cadre clair, identifier les vrais leviers et avancer sereinement.
        </p>
      </SeoSection>

      {/* CTA milieu de page */}
      <section className="my-10 sm:my-12 p-6 sm:p-8 bg-muted/40 border border-border rounded-lg text-center">
        <h2 className="text-xl sm:text-2xl font-cinzel font-bold text-primary mb-3">
          Faire le point sur votre énergie, à Montesquieu-Lauragais
        </h2>
        <div className="w-12 h-px bg-secondary mx-auto mb-4" />
        <p className="text-base text-foreground/80 font-montserrat mb-5">
          Cabinet ouvert sur rendez-vous, à 25 minutes de Toulouse et 10 minutes de Villefranche-de-Lauragais.
        </p>
        <Link to="/#accompagnements">
          <Button size="lg" className="bg-gradient-primary text-primary-foreground hover:opacity-90 h-12 px-6 font-montserrat">
            <Calendar className="w-5 h-5 mr-2" />
            Réserver mon premier rendez-vous
          </Button>
        </Link>
      </section>

      <SeoSection title="Comment se déroule une consultation ?">
        <SeoSubSection title="Le bilan de vitalité (1 h 30)">
          <p>
            La première séance est un temps long, volontairement. C'est un échange en profondeur sur votre histoire,
            votre alimentation, votre sommeil, votre stress, vos antécédents et ce que vous souhaitez améliorer. Si
            cela apporte un éclairage utile, j'utilise l'
            <Link to="/iridologie" className="text-primary underline hover:text-secondary">iridologie</Link> comme
            outil d'observation complémentaire de votre terrain.
          </p>
        </SeoSubSection>
        <SeoSubSection title="Votre programme d'hygiène de vie">
          <p>
            À la suite du bilan, je construis un programme adapté : ajustements alimentaires concrets, routines
            réalistes (matin, soir, semaine), suggestions de plantes, hydrolats, élixirs floraux ou compléments si
            nécessaire. Tout est calibré pour s'inscrire dans votre vie, pas dans un idéal théorique.
          </p>
        </SeoSubSection>
        <SeoSubSection title="Le suivi dans la durée">
          <p>
            Les rendez-vous de suivi (45 min à 1 h) servent à réajuster, à comprendre ce qui a fonctionné, à
            désamorcer les blocages. C'est dans cette régularité que les changements s'ancrent réellement.
          </p>
        </SeoSubSection>
      </SeoSection>

      <SeoSection title="Une zone d'accompagnement étendue dans le Lauragais">
        <p>
          Depuis Montesquieu-Lauragais, j'accompagne des personnes venant de toute la zone du{" "}
          <strong>Lauragais</strong> et du sud toulousain : Villefranche-de-Lauragais, Nailloux, Caraman, Auterive,
          Baziège, Auriac-sur-Vendinelle, Calmont, Cintegabelle, Mauremont, Gardouch, Renneville. La situation
          géographique du cabinet, à proximité de l'A66 et de l'A61, le rend facilement accessible depuis Toulouse
          comme depuis Castelnaudary.
        </p>
        <p>
          Si vous habitez plutôt côté Villefranche, sachez que je consulte également chaque jeudi matin à{" "}
          <Link to="/naturopathe-villefranche-de-lauragais" className="text-primary underline hover:text-secondary">
            Villefranche-de-Lauragais
          </Link>
          , à La Maison Céleste.
        </p>
      </SeoSection>

      <SeoSection title="Pourquoi choisir un cabinet à la campagne ?">
        <p>
          Beaucoup de personnes me confient apprécier le simple fait de <strong>traverser la campagne</strong> pour
          venir en consultation. Le trajet devient une transition : on quitte le rythme de la semaine, on respire,
          on arrive disponible. C'est une dimension à laquelle je tiens — la naturopathie n'est pas qu'un échange
          dans une pièce, c'est une expérience qui commence à la porte du cabinet.
        </p>
        <p>
          Stationnement facile devant le cabinet, environnement calme, pas de salle d'attente bruyante : tout est
          pensé pour que vous arriviez détendu·e.
        </p>
      </SeoSection>

      <SeoSection title="Ce qui guide ma pratique">
        <p>
          Je ne crois pas aux protocoles standardisés, ni aux régimes spectaculaires qui ne tiennent pas trois
          semaines. Ce qui guide ma pratique, c'est un cadre <strong>simple, lisible et tenable</strong>, construit
          autour de votre vraie vie : votre travail, votre famille, votre énergie disponible. J'observe que les
          changements qui durent sont presque toujours ceux qu'on a pu intégrer sans s'épuiser à les tenir.
        </p>
        <p>
          Pour mieux comprendre ma démarche, ma formation et ma vision du métier, vous pouvez consulter{" "}
          <Link to="/mon-approche" className="text-primary underline hover:text-secondary">
            la page « Mon approche »
          </Link>
          .
        </p>
      </SeoSection>

      <SeoSection title="Questions fréquentes">
        <SeoSubSection title="Combien de séances faut-il prévoir ?">
          <p>
            Cela dépend de votre situation. En général, je propose un bilan initial puis 2 à 4 séances de suivi
            espacées de quelques semaines. Certaines personnes viennent ponctuellement pour faire le point, d'autres
            s'inscrivent dans un suivi plus long.
          </p>
        </SeoSubSection>
        <SeoSubSection title="Pour qui c'est adapté ?">
          <p>
            Adultes, adolescents, femmes en périnatalité ou en transition de cycle, parents épuisés, personnes en
            reconversion ou en récupération d'une période difficile. Je reçois aussi les enfants accompagnés de
            leurs parents.
          </p>
        </SeoSubSection>
        <SeoSubSection title="Faut-il venir avec quelque chose ?">
          <p>
            Vos derniers bilans sanguins si vous en avez, la liste de vos traitements en cours, et surtout vos
            ressentis. Pas besoin de tout préparer : nous construisons le bilan ensemble.
          </p>
        </SeoSubSection>
      </SeoSection>

      {/* CTA fin de page */}
      <section className="mt-10 sm:mt-12 p-6 sm:p-8 bg-gradient-to-br from-primary/5 to-secondary/5 border border-border rounded-lg text-center">
        <h2 className="text-xl sm:text-2xl font-cinzel font-bold text-primary mb-3">
          Commencer un accompagnement adapté à votre rythme
        </h2>
        <div className="w-12 h-px bg-secondary mx-auto mb-4" />
        <p className="text-base text-foreground/80 font-montserrat mb-5 max-w-2xl mx-auto">
          Prendre rendez-vous à Montesquieu-Lauragais, c'est s'offrir un temps pour faire le point, dans un cadre
          calme, à quelques minutes de chez vous.
        </p>
        <Link to="/#accompagnements">
          <Button size="lg" className="bg-gradient-primary text-primary-foreground hover:opacity-90 h-12 px-6 font-montserrat">
            <Calendar className="w-5 h-5 mr-2" />
            Prendre rendez-vous à Montesquieu-Lauragais
          </Button>
        </Link>
      </section>
    </SeoPageLayout>
  );
};

export default NaturopatheMontesquieu;
